import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, Eye, Send, FileText, ArrowRight, Trash2, Edit, Download,
  ChevronLeft, ChevronRight, RefreshCw, XCircle, Undo2, Check, MessageSquare
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEstimates() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [estimates, setEstimates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  
  const [form, setForm] = useState({
    customer_id: '',
    items: [{ product_id: '', description: '', quantity: 1, unit_price: 0 }],
    notes: '',
    internal_notes: '',
    valid_days: 30
  });

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchEstimates();
    fetchCustomers();
    fetchProducts();
  }, []);

  // Handle search highlight - auto-open estimate from global search
  useEffect(() => {
    const highlightId = searchParams.get('highlight') || location.state?.highlightId;
    if (!highlightId) return;

    const openEstimate = async (id) => {
      let estimate = estimates.find(e => e.id === id);
      if (!estimate) {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API}/admin/estimates/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          estimate = res.data;
        } catch { return; }
      }
      if (estimate) {
        setSelectedEstimate(estimate);
        setViewDialogOpen(true);
      }
      setSearchParams({}, { replace: true });
      window.history.replaceState({}, '');
    };

    openEstimate(highlightId);
  }, [estimates, searchParams, location.state]);

  const fetchEstimates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/estimates`, authHeaders);
      setEstimates(res.data);
    } catch (err) {
      console.error('Error fetching estimates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/admin/customers/all`, authHeaders);
      // API returns { customers: [...], stats: {...} }
      setCustomers(res.data.customers || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/admin/products?limit=100`, authHeaders);
      setProducts(res.data.products || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [...form.items, { product_id: '', description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const removeItem = (index) => {
    setForm({
      ...form,
      items: form.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    // Auto-calculate total
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    setForm({ ...form, items: newItems });
  };

  const calculateTotal = () => {
    const subtotal = form.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const tax = subtotal * 0.1;
    return { subtotal, tax, total: subtotal + tax };
  };

  const handleSubmit = async () => {
    if (!form.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (form.items.length === 0 || !form.items[0].description) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      const itemsWithTotals = form.items.map(item => ({
        ...item,
        total: item.quantity * item.unit_price
      }));

      await axios.post(`${API}/admin/estimates`, {
        ...form,
        items: itemsWithTotals
      }, authHeaders);
      toast.success('Estimate created');
      setDialogOpen(false);
      setForm({
        customer_id: '',
        items: [{ description: '', quantity: 1, unit_price: 0 }],
        notes: '',
        internal_notes: '',
        valid_days: 30
      });
      fetchEstimates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create estimate');
    }
  };

  const sendEstimate = async (estimateId) => {
    try {
      await axios.post(`${API}/admin/estimates/${estimateId}/send`, {}, authHeaders);
      toast.success('Estimate sent to customer');
      fetchEstimates();
    } catch (err) {
      toast.error('Failed to send estimate');
    }
  };

  const declineEstimate = async (estimateId) => {
    if (!window.confirm('Are you sure you want to decline this estimate?')) return;
    try {
      await axios.post(`${API}/admin/estimates/${estimateId}/decline`, {}, authHeaders);
      toast.success('Estimate declined');
      fetchEstimates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline estimate');
    }
  };

  const unapproveEstimate = async (estimateId) => {
    if (!window.confirm('Are you sure you want to unapprove this estimate? It will be reverted to "sent" status.')) return;
    try {
      await axios.post(`${API}/admin/estimates/${estimateId}/unapprove`, {}, authHeaders);
      toast.success('Estimate unapproved - reverted to sent status');
      fetchEstimates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unapprove estimate');
    }
  };

  const deleteEstimate = async (estimateId) => {
    if (!window.confirm('Are you sure you want to delete this estimate? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/estimates/${estimateId}`, authHeaders);
      toast.success('Estimate deleted');
      fetchEstimates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete estimate');
    }
  };

  const convertToInvoice = async (estimateId) => {
    try {
      const res = await axios.post(`${API}/admin/estimates/${estimateId}/convert`, {}, authHeaders);
      toast.success(`Converted to invoice ${res.data.invoice_number}`);
      fetchEstimates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert estimate');
    }
  };

  const approveEstimate = async (estimateId) => {
    try {
      const res = await axios.post(`${API}/admin/estimates/${estimateId}/approve`, {}, authHeaders);
      if (res.data.invoice_number) {
        toast.success(`Approved & converted to invoice ${res.data.invoice_number} (synced to Xero)`);
      } else {
        toast.success('Estimate approved');
      }
      fetchEstimates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const downloadPDF = async (estimateId, estimateNumber) => {
    try {
      const res = await axios.get(`${API}/admin/estimates/${estimateId}/pdf`, {
        ...authHeaders,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `estimate-${estimateNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-zinc-500/20 text-zinc-500';
      case 'sent': return 'bg-blue-500/20 text-blue-500';
      case 'accepted': return 'bg-[#78BE20]/20 text-[#78BE20]';
      case 'rejected': return 'bg-red-500/20 text-red-500';
      case 'declined': return 'bg-red-500/20 text-red-500';
      case 'expired': return 'bg-orange-500/20 text-orange-500';
      case 'converted': return 'bg-purple-500/20 text-purple-500';
      default: return 'bg-zinc-500/20 text-zinc-500';
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-estimates">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black text-white uppercase tracking-tight">
              Estimates
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Create and send estimates to customers
            </p>
          </div>
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Estimate
          </Button>
        </div>

        {/* Estimates Table */}
        <div className="bg-[#18181b] border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Estimate #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Ticket</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Valid Until</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Created</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={8} className="px-4 py-4">
                      <div className="h-8 bg-zinc-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : estimates.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-zinc-500">
                    No estimates yet. Create your first estimate!
                  </td>
                </tr>
              ) : (
                estimates.map(estimate => (
                  <tr key={estimate.id} className="hover:bg-zinc-900/50">
                    <td className="px-4 py-4">
                      <p className="text-white font-mono">{estimate.estimate_number}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-white">{estimate.customer_name}</p>
                      <p className="text-xs text-zinc-500">{estimate.customer_email}</p>
                    </td>
                    <td className="px-4 py-4">
                      {estimate.source_ticket_id ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/tickets/${estimate.source_ticket_id}`)}
                          className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 px-2 py-1 h-auto"
                          data-testid={`estimate-ticket-link-${estimate.id}`}
                        >
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {estimate.source_ticket_id.startsWith('TKT-') ? estimate.source_ticket_id : 'View Ticket'}
                        </Button>
                      ) : (
                        <span className="text-zinc-600 text-sm">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <Badge className={getStatusColor(estimate.status)}>{estimate.status}</Badge>
                    </td>
                    <td className="px-4 py-4 text-[#78BE20] font-mono font-bold">
                      ${estimate.total?.toFixed(2)}
                    </td>
                    <td className="px-4 py-4 text-zinc-400 text-sm">
                      {estimate.valid_until || 'N/A'}
                    </td>
                    <td className="px-4 py-4 text-zinc-400 text-sm">
                      {new Date(estimate.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 text-right space-x-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => { setSelectedEstimate(estimate); setViewDialogOpen(true); }}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => downloadPDF(estimate.id, estimate.estimate_number)}
                        className="text-zinc-400 hover:text-white"
                        title="Download PDF"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      {estimate.status === 'draft' && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => sendEstimate(estimate.id)}
                          className="text-zinc-400 hover:text-blue-500"
                          title="Send Estimate"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      )}
                      {estimate.status === 'sent' && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => sendEstimate(estimate.id)}
                          className="text-zinc-400 hover:text-blue-500"
                          title="Resend Estimate"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      )}
                      {estimate.status === 'sent' && !estimate.converted_to_invoice_id && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => approveEstimate(estimate.id)}
                          className="text-zinc-400 hover:text-green-500"
                          title="Approve & Convert to Invoice"
                          data-testid={`approve-estimate-${estimate.id}`}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      {(estimate.status === 'sent' || estimate.status === 'accepted') && !estimate.converted_to_invoice_id && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => convertToInvoice(estimate.id)}
                          className="text-zinc-400 hover:text-[#78BE20]"
                          title="Convert to Invoice"
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      )}
                      {estimate.status === 'accepted' && !estimate.converted_to_invoice_id && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => unapproveEstimate(estimate.id)}
                          className="text-zinc-400 hover:text-yellow-500"
                          title="Unapprove (Revert to Sent)"
                        >
                          <Undo2 className="h-4 w-4" />
                        </Button>
                      )}
                      {estimate.status !== 'converted' && estimate.status !== 'declined' && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => declineEstimate(estimate.id)}
                          className="text-zinc-400 hover:text-orange-500"
                          title="Decline Estimate"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      )}
                      {estimate.status !== 'converted' && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => deleteEstimate(estimate.id)}
                          className="text-zinc-400 hover:text-red-500"
                          title="Delete Estimate"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Create Estimate Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Estimate</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Customer</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({...form, customer_id: v})}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name || `${c.first_name} ${c.last_name}`} ({c.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Line Items</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={addItem} className="text-[#78BE20]">
                    <Plus className="h-4 w-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.items.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <div className="flex-1">
                        <Select
                          value={item.product_id || ''}
                          onValueChange={(value) => {
                            if (value === 'custom') {
                              const newItems = [...form.items];
                              newItems[index] = {
                                ...newItems[index],
                                product_id: 'custom',
                                description: '',
                                unit_price: 0
                              };
                              setForm({ ...form, items: newItems });
                            } else {
                              const product = products.find(p => p.id === value);
                              if (product) {
                                const newItems = [...form.items];
                                newItems[index] = {
                                  ...newItems[index],
                                  product_id: product.id,
                                  description: product.name,
                                  unit_price: product.retail_price || product.pricing?.retail || 0,
                                  sku: product.sku
                                };
                                newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
                                setForm({ ...form, items: newItems });
                              }
                            }
                          }}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue placeholder="Select a product..." />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            <SelectItem value="custom">Custom Item (Manual Entry)</SelectItem>
                            {products.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name} - ${(p.retail_price || p.pricing?.retail || 0).toFixed(2)} ({p.sku})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {item.product_id === 'custom' && (
                          <Input
                            placeholder="Custom item description"
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            className="mt-2 bg-zinc-800 border-zinc-700"
                          />
                        )}
                      </div>
                      <div className="w-20">
                        <Input
                          type="number"
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                          className="bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <div className="w-28">
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Unit Price"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <div className="w-28 text-right pt-2 text-[#78BE20] font-mono">
                        ${(item.quantity * item.unit_price).toFixed(2)}
                      </div>
                      {form.items.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(index)} className="text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-zinc-800 rounded-lg p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-400">Subtotal</span>
                  <span className="text-white font-mono">${calculateTotal().subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-400">GST (10%)</span>
                  <span className="text-white font-mono">${calculateTotal().tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-zinc-700 pt-2 mt-2">
                  <span className="text-white">Total</span>
                  <span className="text-[#78BE20] font-mono">${calculateTotal().total.toFixed(2)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valid For (Days)</Label>
                  <Input
                    type="number"
                    value={form.valid_days}
                    onChange={(e) => setForm({...form, valid_days: parseInt(e.target.value) || 30})}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>

              <div>
                <Label>Notes (Visible to Customer)</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  rows={2}
                />
              </div>

              <div>
                <Label>Internal Notes</Label>
                <Textarea
                  value={form.internal_notes}
                  onChange={(e) => setForm({...form, internal_notes: e.target.value})}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  rows={2}
                  placeholder="Internal notes (not visible to customer)"
                />
              </div>

              <div className="flex gap-2 pt-4 border-t border-zinc-800">
                <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-[#78BE20] text-black hover:bg-[#65a31b]" onClick={handleSubmit}>
                  Create Estimate
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Estimate Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">
                Estimate {selectedEstimate?.estimate_number}
              </DialogTitle>
            </DialogHeader>
            {selectedEstimate && (
              <div className="space-y-4 mt-4">
                <div className="flex items-center gap-2">
                  <Badge className={getStatusColor(selectedEstimate.status)}>{selectedEstimate.status}</Badge>
                  {selectedEstimate.converted_to_invoice_id && (
                    <a href="/admin/invoices" className="inline-flex items-center gap-1 bg-purple-500/20 text-purple-500 text-xs px-2 py-1 rounded hover:bg-purple-500/30 transition-colors">
                      <FileText className="h-3 w-3" /> View Invoice
                    </a>
                  )}
                  {selectedEstimate.source_ticket_id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setViewDialogOpen(false); navigate(`/admin/tickets/${selectedEstimate.source_ticket_id}`); }}
                      className="inline-flex items-center gap-1 bg-blue-500/20 text-blue-400 text-xs px-2 py-1 h-auto rounded hover:bg-blue-500/30"
                    >
                      <MessageSquare className="h-3 w-3" /> View Ticket
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-zinc-400 text-sm">Customer</p>
                    <p className="text-white">{selectedEstimate.customer_name}</p>
                    <p className="text-zinc-500 text-sm">{selectedEstimate.customer_email}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Valid Until</p>
                    <p className="text-white">{selectedEstimate.valid_until || 'N/A'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-zinc-400 text-sm mb-2">Items</p>
                  <div className="bg-zinc-800 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-zinc-700">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs text-zinc-400">Description</th>
                          <th className="px-3 py-2 text-right text-xs text-zinc-400">Qty</th>
                          <th className="px-3 py-2 text-right text-xs text-zinc-400">Price</th>
                          <th className="px-3 py-2 text-right text-xs text-zinc-400">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEstimate.items?.map((item, i) => (
                          <tr key={i} className="border-t border-zinc-700">
                            <td className="px-3 py-2 text-white text-sm">{item.description}</td>
                            <td className="px-3 py-2 text-zinc-400 text-sm text-right">{item.quantity}</td>
                            <td className="px-3 py-2 text-zinc-400 text-sm text-right">${item.unit_price?.toFixed(2)}</td>
                            <td className="px-3 py-2 text-white text-sm text-right">${item.total?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex justify-end gap-8 text-sm">
                  <div>
                    <p className="text-zinc-400">Subtotal: <span className="text-white font-mono">${selectedEstimate.subtotal?.toFixed(2)}</span></p>
                    <p className="text-zinc-400">GST: <span className="text-white font-mono">${selectedEstimate.tax?.toFixed(2)}</span></p>
                    <p className="text-lg font-bold text-[#78BE20] mt-1">Total: ${selectedEstimate.total?.toFixed(2)}</p>
                  </div>
                </div>

                {selectedEstimate.notes && (
                  <div>
                    <p className="text-zinc-400 text-sm">Notes</p>
                    <p className="text-white text-sm">{selectedEstimate.notes}</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-4 border-t border-zinc-800">
                  <Button 
                    variant="outline" 
                    className="border-zinc-700"
                    onClick={() => downloadPDF(selectedEstimate.id, selectedEstimate.estimate_number)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                  
                  {/* Send/Resend button */}
                  {selectedEstimate.status === 'draft' && (
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => { sendEstimate(selectedEstimate.id); setViewDialogOpen(false); }}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Send to Customer
                    </Button>
                  )}
                  {selectedEstimate.status === 'sent' && (
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => { sendEstimate(selectedEstimate.id); setViewDialogOpen(false); }}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Resend
                    </Button>
                  )}
                  
                  {/* Approve & Convert button - for sent estimates */}
                  {selectedEstimate.status === 'sent' && !selectedEstimate.converted_to_invoice_id && (
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => { 
                        approveEstimate(selectedEstimate.id);
                        setViewDialogOpen(false);
                      }}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Approve & Convert to Invoice
                    </Button>
                  )}
                  
                  {/* Unapprove button - for accepted estimates */}
                  {selectedEstimate.status === 'accepted' && !selectedEstimate.converted_to_invoice_id && (
                    <Button 
                      variant="outline"
                      className="border-yellow-500 text-yellow-500 hover:bg-yellow-500/10"
                      onClick={() => { unapproveEstimate(selectedEstimate.id); setViewDialogOpen(false); }}
                    >
                      <Undo2 className="h-4 w-4 mr-2" />
                      Unapprove
                    </Button>
                  )}
                  
                  {/* Convert to Invoice */}
                  {(selectedEstimate.status === 'sent' || selectedEstimate.status === 'accepted') && !selectedEstimate.converted_to_invoice_id && (
                    <Button 
                      className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                      onClick={() => { convertToInvoice(selectedEstimate.id); setViewDialogOpen(false); }}
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Convert to Invoice
                    </Button>
                  )}
                  
                  {/* Decline button */}
                  {(selectedEstimate.status === 'sent' || selectedEstimate.status === 'accepted') && !selectedEstimate.converted_to_invoice_id && (
                    <Button 
                      variant="outline"
                      className="border-red-500 text-red-500 hover:bg-red-500/10"
                      onClick={() => { declineEstimate(selectedEstimate.id); setViewDialogOpen(false); }}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Decline
                    </Button>
                  )}
                  
                  {/* Delete button */}
                  {selectedEstimate.status !== 'converted' && (
                    <Button 
                      variant="outline"
                      className="border-red-500 text-red-500 hover:bg-red-500/10"
                      onClick={() => { deleteEstimate(selectedEstimate.id); setViewDialogOpen(false); }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
